import {
  Card,
  GetAllDashboardsResponse,
  StarDashboardRequest,
  MarkDashboardViewedRequest,
  GetInitialDashboardsResponse,
  DuplicateCardResponse,
  DuplicateCardRequest,
  DuplicateSpaceDashboardCardRequest,
  DuplicateSpaceDashboardCardResponse,
  UpdateDashboardResponse,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { createRequestReducer, SuccessAction } from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { cloneDeep, filter, mapKeys } from 'lodash';
import { getObjectWithOverlayStorageId } from '../../utils';
import { createAccessReducer } from '../../utils/accessControl';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { CardRequestActions, DashboardActions } from './actions';
import { CardRequestTypes, DashboardRequestTypes, DashboardsById } from './types';
import { SpaceActions, SpaceRequestTypes } from '../spaces';

// the responses have different interfaces / data,
// this type guard helps narrow the type of the action
const isDuplicateCardSuccessAction = (
  action: SuccessAction<
    CardRequestTypes.DuplicateCardSuccess,
    DuplicateCardRequest | DuplicateSpaceDashboardCardRequest,
    DuplicateCardResponse | DuplicateSpaceDashboardCardResponse
  >,
): action is SuccessAction<
  CardRequestTypes.DuplicateCardSuccess,
  DuplicateCardRequest,
  DuplicateCardResponse
> => 'dashboard' in action.data && 'originalCard' in action.data;

function dashboardsById(
  state: DashboardsById = {},
  action: DashboardActions | CardRequestActions | AppBackendActions | SpaceActions,
): DashboardsById {
  switch (action.type) {
    case DashboardRequestTypes.FetchAllDashboardSuccess: {
      const newState: DashboardsById = {
        ...state,
      };
      action.data.dashboards.forEach(d => (newState[d.id as string] = d));
      return newState;
    }
    case DashboardRequestTypes.FetchInitialDashboardsSuccess: {
      const newStateInitialDashboards: DashboardsById = { ...state };
      action.data.recent.forEach(d => (newStateInitialDashboards[d.id as string] = d));
      action.data.starred.forEach(d => (newStateInitialDashboards[d.id as string] = d));
      return newStateInitialDashboards;
    }
    case DashboardRequestTypes.CreateDashboardSuccess:
    case DashboardRequestTypes.DuplicateDashboardSuccess:
    case DashboardRequestTypes.FetchDashboardSuccess:
    case DashboardRequestTypes.CreateFromVirtualDashboardSuccess:
      return {
        ...state,
        [action.data.dashboard?.id as string]: action.data.dashboard!,
      };
    case DashboardRequestTypes.DeleteDashboardSuccess: {
      const { [action.request.body?.dashboardId as string]: toRemove, ...rest } = state;
      return { ...rest };
    }
    case DashboardRequestTypes.BulkDeleteDashboardsSuccess: {
      const body = action.request.body;
      if (!body) {
        return state;
      }
      const nextState = { ...state };
      body.dashboards.forEach(d => {
        delete nextState[d.dashboardId.toString()];
      });
      return nextState;
    }
    case DashboardRequestTypes.UpdateDashboardSuccess:
      return {
        ...state,
        [action.data.dashboard?.id as string]: action.data.dashboard!,
      };
    case DashboardRequestTypes.UpdateDashboardAfterCardMove:
      return {
        ...state,
        [action.dashboard?.id as string]: action.dashboard!,
      };
    case DashboardRequestTypes.MarkDashboardViewedSuccess: {
      const dashboardId = action?.request?.body?.dashboardId as string;
      if (dashboardId) {
        const prevDashboard = state[dashboardId];
        if (!prevDashboard) return state;
        return {
          ...state,
          [dashboardId]: { ...prevDashboard, lastViewed: Timestamp.now() },
        };
      }
      return state;
    }
    case DashboardRequestTypes.StarDashboardSuccess: {
      const dashboardId = action?.request?.body?.dashboardId as string;
      if (!dashboardId) {
        return state;
      }

      const prevDashboard = state[dashboardId];
      if (!prevDashboard) {
        return state;
      }

      return {
        ...state,
        [dashboardId]: { ...prevDashboard, starred: action.request.body!.starred },
      };
    }
    // The dashboard metadata is updated on card update, as well as card order
    case CardRequestTypes.DuplicateCardSuccess: {
      if (isDuplicateCardSuccessAction(action) && action.data.dashboard) {
        const dashboard = action.data.dashboard;
        return {
          ...state,
          [dashboard.id as string]: dashboard,
        };
      }
      return state;
    }
    case CardRequestTypes.CreateCardSuccess:
    case CardRequestTypes.UpdateCardSuccess:
    case CardRequestTypes.DeleteCardSuccess: {
      const dashboard = action.data.dashboard;
      if (!dashboard) {
        throw new Error(
          `Redux action ${action}` +
            ` expected data.dashboard to be defined but got ${dashboard} instead`,
        );
      }
      return {
        ...state,
        [dashboard.id as string]: dashboard,
      };
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (object?.dashboard && object.dashboard.id) {
          updated[object.dashboard.id as string] = object.dashboard;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const dashboards = action.data.objects.reduce<DashboardsById>((acc, o) => {
        if (o.dashboard) acc[o.dashboard.id.toString()] = o.dashboard;
        return acc;
      }, {});

      return Object.keys(dashboards).length
        ? {
            ...state,
            ...dashboards,
          }
        : state;
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
    case AppBackendRequestTypes.GetHomeViewSuccess: {
      if (!action.data.deps?.dashboards) return state;
      return { ...state, ...action.data.deps?.dashboards };
    }
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.dashboards) return state;
      return { ...state, ...action.data.deps.dashboards };
    }
  }
  return state;
}

type InitialDashboardsIds = {
  starred: int64[];
  recent: int64[];
};

function initialDashboardsIds(
  state: InitialDashboardsIds = { starred: [], recent: [] },
  action: DashboardActions,
): InitialDashboardsIds {
  switch (action.type) {
    case DashboardRequestTypes.FetchInitialDashboardsSuccess: {
      return {
        starred: action.data.starred ? action.data.starred.map(d => d.id) : [],
        recent: action.data.recent ? action.data.recent.map(d => d.id) : [],
      };
    }
    case DashboardRequestTypes.StarDashboardSuccess: {
      const body = action.request.body;
      if (!body) {
        throw new Error(
          `Redux action ${action} expected request.body to be defined but got ${body} instead`,
        );
      }
      return updateInitialStarred(state, body);
    }
    case DashboardRequestTypes.MarkDashboardViewedSuccess: {
      const body = action.request.body;
      if (!body) {
        throw new Error(
          `Redux action ${action} expected request.body to be defined but got ${body} instead`,
        );
      }
      return updateInitialRecent(state, body);
    }
    case DashboardRequestTypes.DeleteDashboardSuccess:
      return {
        starred: filter(state.starred, d => d !== action.request.body?.dashboardId),
        recent: filter(state.recent, d => d !== action.request.body?.dashboardId),
      };
  }
  return state;
}

function updateInitialStarred(
  state: InitialDashboardsIds = { starred: [], recent: [] },
  req: StarDashboardRequest,
): InitialDashboardsIds {
  const newState = cloneDeep(state);
  const foundIdx = newState.starred.findIndex((id: int64) => id == req.dashboardId);
  if (req.starred) {
    // We shouldn't be allowed to star an already starred dashboard, but just in case.
    if (foundIdx == -1) {
      newState.starred.push(req.dashboardId);
    }
  } else if (foundIdx >= 0) {
    // Should have found it if we are unstarring, but just to be safe.
    newState.starred.splice(foundIdx, 1);
  }
  return newState;
}

function updateInitialRecent(
  state: InitialDashboardsIds = { starred: [], recent: [] },
  req: MarkDashboardViewedRequest,
): InitialDashboardsIds {
  const recents = (state && state.recent) || [];
  const seenDashboardId = req.dashboardId;
  // If it was already in the list, remove it so we can move it to the top
  const foundIdx = recents.findIndex((id: int64) => id == seenDashboardId);
  if (foundIdx >= 0) {
    recents.splice(foundIdx, 1);
  }

  return {
    ...state,
    // Append to recents
    recent: [seenDashboardId, ...recents],
  };
}

type CardsById = { [id: string]: Card };
function cardsById(
  state: CardsById = {},
  action: CardRequestActions | DashboardActions | AppBackendActions,
): CardsById {
  switch (action.type) {
    case CardRequestTypes.DuplicateCardSuccess: {
      const newState: CardsById = { ...state };
      if (action.data.card) {
        newState[action.data.card.id as string] = action.data.card;
      }
      if (isDuplicateCardSuccessAction(action)) {
        if (action.data && action.data.originalCard) {
          newState[action.data.originalCard.id as string] = action.data.originalCard;
        }
      }
      return newState;
    }
    case CardRequestTypes.UpdateCardSuccess: {
      const card = action.data.card;
      if (!card) {
        throw new Error(
          `Redux action ${action} expected data.card to be defined but got ${card} instead`,
        );
      }
      const keys = Object.keys(state);
      const updatedState: CardsById = {};
      for (const key of keys) {
        /**
         * Overlayed objects will not automatically update with any changes to format or chart type
         * while it is applied without a refetch of the overlay itself. This updates those existing
         * cards when an update happens and an overlay has been applied to any given card.
         */
        if (key.startsWith(`${card.id}-`) && state[key]) {
          updatedState[key] = {
            ...state[key]!,
            format: card.format,
            chartType: card.chartType,
            funnelMetricType: card.funnelMetricType,
          };
        } else if (state[key]) {
          updatedState[key] = state[key]!;
        }
      }
      updatedState[card.id as string] = card;

      return updatedState;
    }
    case CardRequestTypes.CreateCardSuccess:
    case CardRequestTypes.FetchSingleCardSuccess: {
      const card = action.data.card;
      if (!card) {
        throw new Error(
          `Redux action ${action} expected data.card to be defined but got ${card} instead`,
        );
      }
      return {
        ...state,
        [card.id as string]: card,
      };
    }
    case CardRequestTypes.DeleteCardSuccess: {
      const { [action.request.body?.cardId as string]: toRemove, ...rest } = state;
      return { ...rest };
    }

    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
      if (!action.data.deps?.cards) return state;
      if (action.data.overlay && action.data.deps.cards) {
        const cards = action.data.deps.cards;
        const overlayId = action.data.overlay.id;
        const updatedCards = mapKeys(cards, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedCards };
      }
      return { ...state, ...action.data.deps.cards };
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.cards) return state;
      return { ...state, ...action.data.deps.cards };
    }
  }
  return state;
}

const dashboards = createRequestReducer<GetAllDashboardsResponse>()(
  DashboardRequestTypes.FetchAllDashboardRequest,
  DashboardRequestTypes.FetchAllDashboardSuccess,
  DashboardRequestTypes.FetchAllDashboardFailure,
);

const initialDashboards = createRequestReducer<GetInitialDashboardsResponse>()(
  DashboardRequestTypes.FetchInitialDashboardsRequest,
  DashboardRequestTypes.FetchInitialDashboardsSuccess,
  DashboardRequestTypes.FetchInitialDashboardsFailure,
);

const access = createAccessReducer(
  DashboardRequestTypes.DashboardAccessRequest,
  DashboardRequestTypes.DashboardAccessSuccess,
  DashboardRequestTypes.DashboardAccessFailure,
);

const lastUpdatedDashboard = createRequestReducer<UpdateDashboardResponse>()(
  DashboardRequestTypes.UpdateDashboardRequest,
  DashboardRequestTypes.UpdateDashboardSuccess,
  DashboardRequestTypes.UpdateDashboardFailure,
);

const cardLimit = (state: number | null = null, action: AppBackendActions): number | null => {
  switch (action.type) {
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
      return int64.toNumber(action.data.cardLimit);
  }
  return state;
};

type DashboardReducerState = {
  dashboardsById: ReturnType<typeof dashboardsById>;
  dashboards: ReturnType<typeof dashboards>;
  initialDashboardsIds: ReturnType<typeof initialDashboardsIds>;
  initialDashboards: ReturnType<typeof initialDashboards>;
  cardsById: ReturnType<typeof cardsById>;
  access: ReturnType<typeof access>;
  lastUpdatedDashboard: ReturnType<typeof lastUpdatedDashboard>;
  cardLimit: ReturnType<typeof cardLimit>;
};

export const dashboardReducer = combineReducers<DashboardReducerState>({
  dashboardsById,
  dashboards,
  initialDashboardsIds,
  initialDashboards,
  cardsById,
  access,
  lastUpdatedDashboard,
  cardLimit,
});
